/*
 * Copyright (c) 2021-2022. Revo Digital
 * ---
 * Author: gabriele
 * File: LeftPanel.tsx
 * Project: mle-client-n
 * Committed last: 2022/2/9 @ 1735
 * ---
 * Description:
 */

import {
  Button,
  EditableText,
  Icon,
  Intent,
  Tab,
  Tabs,
  Tree,
  TreeNodeInfo
}                                                              from '@blueprintjs/core';
import React, { useCallback, useEffect, useReducer, useState } from 'react';
import {
  Editor
}                                                              from '../../../store/observables/Editor';
import {
  observer
}                                                              from 'mobx-react';
import {
  EntitiesTreeActionTypes,
  entitiesTreeReducer,
  NodePath
}                                                              from '../../../common/tree-utils';
import {
  getShapeTypeFromId,
  shapeIconAssociation
}                                                              from '../../../common/utils';
import {
  ObservableShape
}                                                              from '../../../store/observables/ObservableShape';
import Pamela
                                                               from '@revodigital/pamela';
import {
  HistoryView
}                                                              from './HistoryView';
import {
  OriginFieldsView
}                                                              from './OriginFieldsView';

export const EntityActions = observer((props: { shape: ObservableShape<any>, editor: Editor }) => {
  return (
    <div>
      <Button
        minimal
        icon={ props.shape.visible ? 'eye-on' : 'eye-off' }
        onClick={ () => props.shape.setVisible(!props.shape.visible,
          { applyToPamelaShape: true }) }
        intent={ props.shape.visible ? Intent.NONE : Intent.WARNING }
      />
    </div>
  );
});

export const EntityName = ((props: { name: string, setter: (name: string) => void }) => {
  const [name, setName] = useState(props.name);

  useEffect(() => {
    setName(props.name);
  }, [props.name]);

  return (
    <EditableText
      maxLines={ 1 }
      maxLength={ 25 }
      multiline={ false }
      placeholder="Nome entità"
      confirmOnEnterKey
      selectAllOnFocus
      value={ name }
      onChange={ setName }
      onConfirm={ (newName) => {
        props.setter(newName);
      } }
    />
  );
});

export interface LeftPanelProps {
  editor: Editor;
}

enum ActiveTabIds {
  ENTITIES = 'entità',
  HISTORY = 'Sessione',
  ORIGIN_FIELDS = 'Campi collegati'
}

export const LeftPanel = observer((props: LeftPanelProps) => {
  const [activeTabId, setActiveTabId] = useState(ActiveTabIds.ENTITIES);
  const panFeature = props.editor.pan();

  const formatTabTitle = (selectedTab: ActiveTabIds, titleFor: ActiveTabIds): string => {
    if (selectedTab === titleFor) return titleFor;
    else return '';
  };

  const shapesToNodes = useCallback((): TreeNodeInfo[] => {
    return [{
      id: 0,
      icon: 'label',
      label: 'Etichetta',
      isExpanded: true,
      childNodes: props.editor.shapes.length ? props.editor.shapes.map(
        (shape, index) => ({
          id: index + 1,
          icon: shapeIconAssociation[getShapeTypeFromId(shape.pamelaShape__nonObservable.id() as string)],
          nodeData: shape,
          label: <EntityName name={ shape.name }
                             setter={ name => props.editor.getSelectedShape()?.setName(
                               name,
                               { applyToPamelaShape: true }) }/>,
          secondaryLabel: <EntityActions
            shape={ shape }
            editor={ props.editor }
          />,
          isSelected: props.editor.getSelectedShape()?.pamelaShape__nonObservable.id() === shape.pamelaShape__nonObservable.id()
        })) : [{
        id: 1,
        label: 'Nessuna entità',
        disabled: true
      }],
    }];
  }, [props.editor.shapes, props.editor.getSelectedShape()]);

  const [nodes, dispatch] = useReducer(entitiesTreeReducer, shapesToNodes());

  useEffect(() => {
    dispatch({
      type: EntitiesTreeActionTypes.UPDATE_NODES,
      payload: shapesToNodes()
    });
  }, [props.editor.shapes, props.editor.getSelectedShape()]);

  const handleNodeClick = useCallback(
    (node: TreeNodeInfo<ObservableShape<any>>, nodePath: NodePath, e: React.MouseEvent<HTMLElement>) => {
      const originallySelected = node.isSelected;
      if (!e.shiftKey)
        dispatch({ type: EntitiesTreeActionTypes.DESELECT_ALL });

      if (node && node.nodeData) {
        dispatch({
          payload: {
            path: nodePath,
            isSelected: originallySelected == null ? true : !originallySelected
          },
          type: EntitiesTreeActionTypes.SET_IS_SELECTED,
        });

        props.editor.select(node.nodeData.pamelaShape__nonObservable as Pamela.Shape);
      }
    },
    [],
  );

  const handleNodeCollapse = useCallback((_node: TreeNodeInfo, nodePath: NodePath) => {
    dispatch({
      payload: { path: nodePath, isExpanded: false },
      type: EntitiesTreeActionTypes.SET_IS_EXPANDED,
    });
  }, []);

  const handleNodeExpand = useCallback((_node: TreeNodeInfo, nodePath: NodePath) => {
    dispatch({
      payload: { path: nodePath, isExpanded: true },
      type: EntitiesTreeActionTypes.SET_IS_EXPANDED,
    });
  }, []);

  return (
    <div className="mle-side-panel-wrapper overflow-clip"
         style={ { zIndex: 1 } }>
      <Tabs
        onChange={ newTabId => setActiveTabId(newTabId as ActiveTabIds) }
        selectedTabId={ activeTabId }
        animate
      >
        <Tab
          className="outline-none"
          id={ ActiveTabIds.ENTITIES }
          title={ <div className="capitalize">
            <Icon size={ 12 } icon="list"
                  className="bp4-text-muted mr-1 mb-0.5"/>
            { formatTabTitle(activeTabId, ActiveTabIds.ENTITIES) }
          </div> }
          panel={ <div>
            <div className="bp4-text-muted -mt-2 mb-2">
              Doppio click inquadra la forma
            </div>
            <Tree
              contents={ nodes }
              onNodeClick={ handleNodeClick }
              onNodeDoubleClick={ (node: TreeNodeInfo<ObservableShape>) => panFeature.frameShape(
                node.nodeData?.pamelaShape__nonObservable) }
              onNodeCollapse={ handleNodeCollapse }
              onNodeExpand={ handleNodeExpand }
            />
          </div> }
        />
        <Tab
          className="outline-none"
          id={ ActiveTabIds.HISTORY }
          title={ <div className="capitalize">
            <Icon size={ 12 } icon="history"
                  className="bp4-text-muted mr-1 mb-0.5"/>
            { formatTabTitle(activeTabId, ActiveTabIds.HISTORY) }
          </div> }
          panel={ <HistoryView editor={ props.editor }/> }
        />
        <Tab className="outline-none" id={ ActiveTabIds.ORIGIN_FIELDS }
             title={ <div className="capitalize">
               <Icon size={ 12 } icon="data-connection"
                     className="bp4-text-muted mr-1 mb-0.5"/>
               { formatTabTitle(activeTabId, ActiveTabIds.ORIGIN_FIELDS) }
             </div> } panel={ <OriginFieldsView editor={ props.editor }/> }/>
      </Tabs>
    </div>
  );
});
